import type { Category } from "@/types";

// ============================================================================
// Lector del Catálogo Amway en PDF.
// Extrae, página por página:
//   · SKU (códigos de 5-6 dígitos o alfanuméricos tipo E3878)
//   · Precio público ($XX.XXX / $XXX.XXX)
//   · Nombre y presentación (título + volumen/contenido)
//   · Imagen del envase (objetos de imagen embebidos en la misma página)
// NO calcula los Puntos PV (los PDFs no los traen): quedan en 0 para editarlos.
// ============================================================================

export type CatalogRowStatus = "nuevo" | "cambio" | "foto" | "sin_cambios";

export interface ExtractedImage {
  id: string;
  page: number;
  dataUrl: string;
  x?: number;
  y?: number;
}

export interface PdfCatalogRow {
  id: string;
  page: number;
  y?: number;
  sku: string;
  name: string;
  presentation: string;
  pricePublic: number;
  pv: number;
  category: Category;
  imageUrl: string;
  status: CatalogRowStatus;
}

export interface PdfCatalogPreview {
  rows: PdfCatalogRow[];
  images: ExtractedImage[];
  pages: number;
  suggestedCategory: Category;
  warnings: string[];
}

export interface CatalogImportRow {
  sku: string;
  name: string;
  category: Category;
  pv: number;
  pricePublic: number;
  imageUrl: string;
}

export interface CatalogImportResult {
  created: number;
  updated: number;
  images: number;
  skipped: number;
  warnings: string[];
}

// ------------------------------------------------------------------ helpers
interface PdfLine {
  text: string;
  x: number;
  y: number;
  size: number;
  parts: { text: string; x: number; width: number }[];
}

interface TextItemLike {
  str?: string;
  transform?: number[];
  height?: number;
  width?: number;
}

interface ImageObj {
  width?: number;
  height?: number;
  data?: Uint8Array | Uint8ClampedArray | ArrayBuffer;
  bitmap?: ImageBitmap | null;
}

const SKU_RE = /\b(\d{5,6}|[A-Za-z]{1,2}\d{4,5})\b/g;
const PRICE_RE = /\$\s*(\d[\d.,]{2,})/g;
const PRICE_TEST_RE = /\$\s*\d/;
const PRESENTATION_RE =
  /(\d+(?:[.,]\d+)?)\s?(l|lt|litros?|ml|cc|kg|kgs|g|gr|grs|gramos?|onzas?|oz|lb|unidades?|und|latas?|piezas?|c[aá]psulas?|tabletas?|sobres?|sachets?|rollos?|toallas?|pa[nñ]ales?|barras?|porciones?)\b/i;

const UNIT_CANON: Record<string, string> = {
  l: "L",
  lt: "L",
  litro: "L",
  litros: "L",
  ml: "ml",
  cc: "cc",
  kg: "Kg",
  kgs: "Kg",
  g: "g",
  gr: "g",
  grs: "g",
  gramo: "g",
  gramos: "g",
  onza: "oz",
  onzas: "oz",
  oz: "oz",
  lb: "lb",
  unidad: "und",
  unidades: "und",
  und: "und",
  lata: "latas",
  latas: "latas",
  pieza: "piezas",
  piezas: "piezas",
  capsula: "cáps",
  "cápsula": "cáps",
  capsulas: "cáps",
  "cápsulas": "cáps",
  tableta: "tab",
  tabletas: "tab",
  sobre: "sobre",
  sobres: "sobre",
  sachet: "sobre",
  sachets: "sobre",
  rollo: "rollos",
  rollos: "rollos",
  toalla: "toallas",
  toallas: "toallas",
  pañal: "pañales",
  pañales: "pañales",
  barra: "barras",
  barras: "barras",
  porcion: "porciones",
  porciones: "porciones",
};

const norm = (v: string): string =>
  v
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const parsePrice = (raw: string): number => {
  const cleaned = raw.replace(/[^\d]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const firstPrice = (text: string): number => {
  PRICE_RE.lastIndex = 0;
  const m = PRICE_RE.exec(text);
  return m ? parsePrice(m[1]) : 0;
};

const stripPrice = (text: string): string => text.replace(PRICE_RE, " ").replace(/\s+/g, " ").trim();

const stripSku = (text: string): string => {
  SKU_RE.lastIndex = 0;
  return text.replace(SKU_RE, " ").replace(/\s+/g, " ").trim();
};

const cleanName = (text: string): string => {
  const out = text
    .replace(/[$]/g, " ")
    // Recorta notas al pie / avisos legales que el PDF suele pegar al nombre.
    .replace(/\*\*?.*$/, " ")
    .replace(/\([^)]*\)\s*$/g, " ")
    .replace(/este producto[^.]*/gi, " ")
    .replace(/\(producto exento[^)]*\)/gi, " ")
    .replace(/exento de i\.?\s?v\.?\s?a\.?/gi, " ")
    .replace(/\b(puntos?|pv|p\.v\.|c[oó]digo|ref|referencia|precio|pvp|valor|nombre)\b/gi, " ")
    .replace(/\s+nuevo\s*$/i, " ")
    .replace(/\s+/g, " ")
    .trim();
  // Separa palabras que el PDF dejó pegadas (cambio de mayúscula):
  // "DishDropsScrub" -> "Dish Drops Scrub", "SA8Premium" -> "SA8 Premium".
  return out
    .replace(/([a-z0-9])([A-ZÁÉÍÓÚÑ])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
};

const extractPresentation = (text: string): string => {
  const m = PRESENTATION_RE.exec(text);
  if (!m) return "";
  const qty = m[1].replace(",", ".");
  const unitRaw = norm(m[2]);
  const unit = UNIT_CANON[unitRaw] ?? m[2];
  return `${qty} ${unit}`;
};

const isProbablyJunk = (text: string): boolean => {
  const t = norm(text);
  if (t.length < 3) return true;
  if (/^(hogar|aseo personal|belleza|nutricion|nutrición|amway|cat[aá]logo|productos?|precio|puntos?|sku|c[oó]digo)\b/.test(t)) {
    return true;
  }
  return false;
};

// Encabezados de sección del catálogo (LAVANDERÍA |, SUPERFICIES |, COCINA |...),
// pies de página y etiquetas que nunca son un nombre de producto.
const SECTION_WORDS =
  /^(lavander[ií]a|superficies?|cocina|ba[nñ]os?|accesorios?|aseo|cuidado personal|belleza|nutrici[oó]n|catalogo hogar|hogar|contenido|presentaci[oó]n|descripci[oó]n|detalle|precio|puntos?|safer choice|bioquest|amway|colombia|medell[ií]n|cali|bogot[aá])/i;

const isSectionHeader = (text: string): boolean => {
  const raw = text.trim();
  if (!raw) return true;
  if (/[|│]\s*$/.test(raw)) return true; // "LAVANDERÍA |"
  if (SECTION_WORDS.test(raw)) return true;
  const letters = raw.replace(/[^A-Za-zÁÉÍÓÚÑ]/g, "");
  if (
    letters.length >= 3 &&
    letters === letters.toUpperCase() &&
    raw.split(/\s+/).length <= 3 &&
    raw.length <= 22
  ) {
    return true;
  }
  return false;
};

// Pies de página, avisos legales, teléfonos y datos de contacto del PDF: nunca
// son un producto. Se descartan de raíz para que no entren "productos basura"
// (p. ej. "Línea Gratuita 601 307 8340 o al teléfono 01 8000").
const FOOTER_RE =
  /(l[ií]nea gratuita|tel[eé]fono|\btel\b|whatsapp|servicio al cliente|atenci[oó]n al cliente|pqr|correo|c[oó]digo postal|www\.|https?:|@[a-z]|s[ií]guenos|facebook|instagram|twitter|youtube|radicaci[oó]n|nit\b|vis[ií]tanos|escanea|amway\.com\.co|amway\.com|colombia\.com)/i;

const isFooterOrNoise = (text: string): boolean => {
  const raw = text.trim();
  if (!raw) return true;
  if (FOOTER_RE.test(raw)) return true;
  // Dos o más números largos sueltos (4+ dígitos separados por espacios) son
  // teléfonos/series, no un SKU de producto.
  const longNums = raw.match(/\b\d{4,}\b/g) ?? [];
  if (longNums.length >= 2) return true;
  return false;
};

// ¿La línea puede ser el nombre de un producto?
const isNameCandidate = (text: string): boolean => {
  const raw = text.trim();
  if (!raw) return false;
  const t = norm(raw);
  if (t.length < 3) return false;
  if (/^[\d.,\s$%|│*+\-]+$/.test(raw)) return false; // solo números/símbolos
  if (PRICE_TEST_RE.test(raw)) return false; // es el precio
  if (isSectionHeader(raw)) return false;
  if (isFooterOrNoise(raw)) return false;
  if (isProbablyJunk(raw)) return false;
  return true;
};

export const suggestCategoryFromName = (fileName: string): Category => {
  const f = norm(fileName);
  if (/(belleza|beauty|maquillaje|cosmetic)/.test(f)) return "Belleza";
  if (/(nutri|salud|vitamin|nutrilite|proteina|nutricion)/.test(f)) return "Nutrición";
  if (/(personal|aseo|higiene|cuidado personal|jabon|shampoo)/.test(f)) return "Aseo Personal";
  if (/(hogar|limpieza|casa|lavanderia|detergente)/.test(f)) return "Hogar";
  return "Hogar";
};

// ---------------------------------------------------------------- pdfjs load
type PdfJs = typeof import("pdfjs-dist");

const loadPdfjs = async (): Promise<PdfJs> => {
  const pdfjs = await import("pdfjs-dist");
  const worker = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  return pdfjs;
};

// -------------------------------------------------------------- text -> lines
// Agrupa los fragmentos en líneas (mismo Y) y los ordena de izquierda a
// derecha. En un catálogo en tabla (NOMBRE · CÓDIGO · PRECIO) el nombre queda
// a la izquierda y el precio a la derecha dentro de la misma línea, que es
// justo lo que necesitamos para separarlos.
const buildLines = (items: TextItemLike[]): PdfLine[] => {
  const lines: PdfLine[] = [];
  items.forEach((item) => {
    const str = item.str ?? "";
    if (!str.trim() || !item.transform) return;
    const x = item.transform[4] ?? 0;
    const y = item.transform[5] ?? 0;
    const size = Math.abs(item.height ?? item.transform[3] ?? 0);
    const width = item.width ?? Math.hypot(item.transform[0] ?? 0, item.transform[1] ?? 0);
    let line = lines.find((l) => Math.abs(l.y - y) < 3.5);
    if (!line) {
      line = { text: "", x, y, size, parts: [] };
      lines.push(line);
    }
    line.parts.push({ text: str, x, width: width || 0 });
    line.x = Math.min(line.x, x);
    line.size = Math.max(line.size, size);
  });
  lines.forEach((l) => {
    l.parts.sort((a, b) => a.x - b.x);
    l.text = l.parts
      .map((p) => p.text)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  });
  lines.sort((a, b) => b.y - a.y || a.x - b.x);
  return lines.filter((l) => l.text);
};

// ----------------------------------------------------------------- images
const getImageObj = (
  page: { objs?: unknown; commonObjs?: unknown },
  name: string
): Promise<ImageObj | null> =>
  new Promise((resolve) => {
    let settled = false;
    const done = (v: ImageObj | null) => {
      if (settled) return;
      settled = true;
      resolve(v);
    };
    const timer = setTimeout(() => done(null), 2500);
    const stores = [page.objs, page.commonObjs].filter(Boolean) as {
      get: (n: string, cb?: (o: ImageObj) => void) => unknown;
    }[];
    for (const store of stores) {
      if (!store || typeof store.get !== "function") continue;
      // 1) Si el objeto ya está resuelto, se obtiene de forma síncrona.
      try {
        const sync = store.get(name);
        if (sync && typeof sync === "object") {
          clearTimeout(timer);
          done(sync as ImageObj);
          return;
        }
      } catch {
        /* aún no resuelto: usamos el callback */
      }
      // 2) La forma correcta y universal en pdfjs v6: con callback.
      try {
        store.get(name, (obj) => {
          clearTimeout(timer);
          done((obj as ImageObj) ?? null);
        });
        return;
      } catch {
        /* intenta el siguiente store */
      }
    }
    clearTimeout(timer);
    done(null);
  });

const rasterToDataUrl = async (img: ImageObj): Promise<string> => {
  try {
    const width = img.width ?? img.bitmap?.width ?? 0;
    const height = img.height ?? img.bitmap?.height ?? 0;
    if (width < 40 || height < 40) return ""; // ignora íconos/viñetas
    const max = 360;
    const scale = Math.min(1, max / Math.max(width, height));
    const cw = Math.max(1, Math.round(width * scale));
    const ch = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";

    const data = img.data;
    const px = width * height;
    let rgba: Uint8ClampedArray | null = null;
    if (data && (data instanceof Uint8ClampedArray || data instanceof Uint8Array)) {
      if (data.length === px * 4) {
        rgba =
          data instanceof Uint8ClampedArray
            ? data
            : new Uint8ClampedArray(data.buffer, data.byteOffset, data.length);
      } else if (data.length === px * 3) {
        rgba = new Uint8ClampedArray(px * 4);
        for (let i = 0; i < px; i += 1) {
          rgba[i * 4] = data[i * 3];
          rgba[i * 4 + 1] = data[i * 3 + 1];
          rgba[i * 4 + 2] = data[i * 3 + 2];
          rgba[i * 4 + 3] = 255;
        }
      } else if (data.length === px) {
        rgba = new Uint8ClampedArray(px * 4);
        for (let i = 0; i < px; i += 1) {
          const v = data[i];
          rgba[i * 4] = v;
          rgba[i * 4 + 1] = v;
          rgba[i * 4 + 2] = v;
          rgba[i * 4 + 3] = 255;
        }
      }
    }

    if (rgba) {
      const tmp = document.createElement("canvas");
      tmp.width = width;
      tmp.height = height;
      const tctx = tmp.getContext("2d");
      if (!tctx) return "";
      const imgData = tctx.createImageData(width, height);
      imgData.data.set(rgba);
      tctx.putImageData(imgData, 0, 0);
      ctx.drawImage(tmp, 0, 0, cw, ch);
      return canvas.toDataURL("image/jpeg", 0.72);
    }

    if (img.bitmap) {
      ctx.drawImage(img.bitmap, 0, 0, cw, ch);
      return canvas.toDataURL("image/jpeg", 0.72);
    }

    if (data) {
      const bytes = data instanceof ArrayBuffer ? new Uint8Array(data) : data;
      const blob = new Blob([bytes], { type: "image/png" });
      const bmp = await createImageBitmap(blob);
      ctx.drawImage(bmp, 0, 0, cw, ch);
      if (typeof bmp.close === "function") bmp.close();
      return canvas.toDataURL("image/jpeg", 0.72);
    }

    return "";
  } catch {
    return "";
  }
};

// --- Seguimiento de la matriz de transformación (para ubicar cada imagen) ---
type Mat = [number, number, number, number, number, number];
const IDENTITY_MAT: Mat = [1, 0, 0, 1, 0, 0];
const matMul = (m1: Mat, m2: Mat): Mat => [
  m1[0] * m2[0] + m1[2] * m2[1],
  m1[1] * m2[0] + m1[3] * m2[1],
  m1[0] * m2[2] + m1[2] * m2[3],
  m1[1] * m2[2] + m1[3] * m2[3],
  m1[0] * m2[4] + m1[2] * m2[5] + m1[4],
  m1[1] * m2[4] + m1[3] * m2[5] + m1[5],
];

interface PendingImage {
  name: string;
  x: number;
  y: number;
}

// Lee el identificador del objeto de imagen. En pdfjs v6 el argumento de
// paintImageXObject llega como una lista (["img_0"]), no como texto plano.
const readImageName = (raw: unknown): string => {
  if (typeof raw === "string") return raw;
  if (Array.isArray(raw) && typeof raw[0] === "string") return raw[0];
  if (raw && typeof raw === "object" && "data" in (raw as object)) {
    const d = (raw as { data?: unknown }).data;
    if (typeof d === "string") return d;
  }
  return "";
};

const extractPageImages = async (
  pdfjs: PdfJs,
  page: {
    getOperatorList: () => Promise<{ fnArray: number[]; argsArray: unknown[] }>;
    objs?: unknown;
    commonObjs?: unknown;
  },
  pageNum: number,
  counter: { n: number }
): Promise<ExtractedImage[]> => {
  const out: ExtractedImage[] = [];
  try {
    const opList = await page.getOperatorList();
    const OPS = pdfjs.OPS as unknown as Record<string, number>;
    let ctm: Mat = [...IDENTITY_MAT] as Mat;
    const stack: Mat[] = [];
    const pending: PendingImage[] = [];
    const inline: { obj: ImageObj; x: number; y: number }[] = [];

    for (let i = 0; i < opList.fnArray.length; i += 1) {
      const fn = opList.fnArray[i];
      const args = opList.argsArray[i];

      if (fn === OPS.transform) {
        const a = args as number[];
        ctm = matMul(ctm, [a[0], a[1], a[2], a[3], a[4], a[5]] as Mat);
        continue;
      }
      if (fn === OPS.save) {
        stack.push([...ctm] as Mat);
        continue;
      }
      if (fn === OPS.restore) {
        const m = stack.pop();
        if (m) ctm = m;
        continue;
      }

      if (fn === OPS.paintImageXObject || fn === OPS.paintJpegXObject) {
        const name = readImageName(args);
        if (name) pending.push({ name, x: ctm[4], y: ctm[5] });
      } else if (fn === OPS.paintImageXObjectRepeat) {
        const raw = Array.isArray(args) ? (args as unknown[]) : [args];
        const name = readImageName(raw[0]);
        if (name) {
          let coords: number[] = [];
          if (raw[3] && typeof (raw[3] as ArrayLike<number>).length === "number") {
            coords = Array.from(raw[3] as ArrayLike<number>).map(Number);
          } else {
            coords = raw.slice(3).map((v) => Number(v));
          }
          for (let k = 0; k + 1 < coords.length; k += 2) {
            const px = coords[k];
            const py = coords[k + 1];
            pending.push({
              name,
              x: ctm[0] * px + ctm[2] * py + ctm[4],
              y: ctm[1] * px + ctm[3] * py + ctm[5],
            });
          }
        }
      } else if (fn === OPS.paintInlineImageXObject) {
        const arg = Array.isArray(args) ? args[0] : args;
        if (arg && typeof arg === "object" && "data" in (arg as object) && "width" in (arg as object)) {
          inline.push({ obj: arg as ImageObj, x: ctm[4], y: ctm[5] });
        }
      }
    }

    for (const item of inline) {
      const url = await rasterToDataUrl(item.obj);
      if (url) {
        counter.n += 1;
        out.push({
          id: `img_${pageNum}_${counter.n}`,
          page: pageNum,
          dataUrl: url,
          x: item.x,
          y: item.y,
        });
      }
    }
    for (const item of pending) {
      const obj = await getImageObj(page, item.name);
      if (!obj) continue;
      const url = await rasterToDataUrl(obj);
      if (url) {
        counter.n += 1;
        out.push({
          id: `img_${pageNum}_${counter.n}`,
          page: pageNum,
          dataUrl: url,
          x: item.x,
          y: item.y,
        });
      }
    }
  } catch {
    /* página sin imágenes extraíbles */
  }
  return out;
};

// ------------------------------------------------------------- product parse
// Este catálogo ("Lista de precios | Clientes") viene como TABLA con las
// columnas NOMBRE · CÓDIGO · PRECIO. En cada fila el nombre, el código y el
// precio comparten la misma línea, así que el nombre es lo que queda al
// quitar el SKU y el precio (el precio siempre va después de un "$").
const parsePageRows = (
  lines: PdfLine[],
  page: number,
  category: Category
): { rows: PdfCatalogRow[]; ignored: number } => {
  const rows: PdfCatalogRow[] = [];
  const usedSkus = new Set<string>();
  let ignored = 0;

  const firstSku = (text: string): string => {
    SKU_RE.lastIndex = 0;
    const m = SKU_RE.exec(text);
    return m ? m[1] : "";
  };

  lines.forEach((line, idx) => {
    // Filtro duro anti-basura: pies de página, teléfonos y texto legal se
    // descartan antes de intentar leer un producto.
    if (isFooterOrNoise(line.text)) {
      if (firstSku(line.text)) ignored += 1;
      return;
    }
    const sku = firstSku(line.text);
    if (!sku || usedSkus.has(sku)) return;

    // Nombre: el texto de la misma fila sin el SKU ni el precio.
    let name = cleanName(stripSku(stripPrice(line.text)));
    if (!isNameCandidate(name)) name = "";

    let price = firstPrice(line.text);

    // Respaldo de nombre: si no vino en la misma fila, buscar en las líneas
    // contiguas (arriba o abajo) hasta el producto anterior/siguiente.
    if (!name) {
      for (let j = idx - 1; j >= 0 && idx - j <= 8; j -= 1) {
        if (firstSku(lines[j].text)) break;
        if (!isNameCandidate(lines[j].text)) continue;
        name = cleanName(stripSku(stripPrice(lines[j].text)));
        if (name) break;
      }
    }
    if (!name) {
      for (let j = idx + 1; j < lines.length && j - idx <= 8; j += 1) {
        if (firstSku(lines[j].text)) break;
        if (!isNameCandidate(lines[j].text)) continue;
        name = cleanName(stripSku(stripPrice(lines[j].text)));
        if (name) break;
      }
    }
    const hadRealName = !!name;
    if (!name) name = `Producto ${sku}`;

    // Respaldo de precio: la línea con "$" más cercana (normalmente la misma).
    if (!price) {
      for (let j = idx - 1; j >= 0 && idx - j <= 6; j -= 1) {
        const p = firstPrice(lines[j].text);
        if (p) {
          price = p;
          break;
        }
        if (firstSku(lines[j].text)) break;
      }
    }

    // Sin nombre real ni precio => no es un producto (renglón suelto/basura).
    if (!hadRealName && !price) {
      ignored += 1;
      return;
    }

    usedSkus.add(sku);
    rows.push({
      id: `pdf_${page}_${sku}_${rows.length}`,
      page,
      y: line.y,
      sku,
      name,
      presentation: extractPresentation(line.text) || extractPresentation(name),
      pricePublic: price,
      pv: 0,
      category,
      imageUrl: "",
      status: "nuevo",
    });
  });

  return { rows, ignored };
};

// -------------------------------------------------------------------- main
export interface ParsePdfOptions {
  category?: Category;
  fileName?: string;
  // Extraer las fotos embebidas del PDF. Desactivado por defecto: las fotos se
  // cargan a mano (subir/URL) para que la lectura sea rápida y confiable.
  extractImages?: boolean;
}

export const parsePdfCatalog = async (
  file: File,
  options: ParsePdfOptions = {}
): Promise<PdfCatalogPreview> => {
  const warnings: string[] = [];
  const pdfjs = await loadPdfjs();
  const data = new Uint8Array(await file.arrayBuffer());
  const doc = await pdfjs.getDocument({ data }).promise;

  const suggestedCategory = options.category ?? suggestCategoryFromName(options.fileName ?? file.name);
  const wantImages = options.extractImages === true;
  const rows: PdfCatalogRow[] = [];
  const images: ExtractedImage[] = [];
  const counter = { n: 0 };
  let ignoredLines = 0;

  for (let p = 1; p <= doc.numPages; p += 1) {
    const page = await doc.getPage(p);
    try {
      const content = await page.getTextContent();
      const pageLines = buildLines(content.items as TextItemLike[]);
      const parsed = parsePageRows(pageLines, p, suggestedCategory);
      rows.push(...parsed.rows);
      ignoredLines += parsed.ignored;
    } catch {
      warnings.push(`No se pudo leer el texto de la página ${p}.`);
    }
    if (wantImages) {
      try {
        const pageImages = await extractPageImages(pdfjs, page, p, counter);
        images.push(...pageImages);
      } catch {
        /* sin imágenes en esta página */
      }
    }
  }

  if (rows.length === 0) {
    warnings.push(
      "No se detectaron SKU/precios. Puede que el PDF sea una imagen escaneada (sin texto seleccionable) o tenga otro formato."
    );
  } else {
    const sinNombre = rows.filter((r) => /^Producto\s/.test(r.name)).length;
    const sinPrecio = rows.filter((r) => !r.pricePublic).length;
    if (sinNombre > 0 || sinPrecio > 0) {
      const parts: string[] = [];
      if (sinNombre > 0) parts.push(`${sinNombre} sin nombre`);
      if (sinPrecio > 0) parts.push(`${sinPrecio} sin precio`);
      warnings.push(
        `Detectados ${rows.length} productos, pero hay ${parts.join(" y ")}. Complétalos a mano en la tabla.`
      );
    }
  }

  if (ignoredLines > 0) {
    warnings.push(
      `Se ignoraron ${ignoredLines} líneas que no son productos (pies de página, teléfonos, avisos legales o datos de contacto), para que no entren como productos basura.`
    );
  }

  // Asociación automática de imágenes por página (editable luego).
  const imagesByPage = new Map<number, ExtractedImage[]>();
  images.forEach((img) => {
    const list = imagesByPage.get(img.page) ?? [];
    list.push(img);
    imagesByPage.set(img.page, list);
  });
  const rowsByPage = new Map<number, PdfCatalogRow[]>();
  rows.forEach((r) => {
    const list = rowsByPage.get(r.page) ?? [];
    list.push(r);
    rowsByPage.set(r.page, list);
  });
  rowsByPage.forEach((list, pageNum) => {
    const imgs = imagesByPage.get(pageNum) ?? [];
    if (imgs.length === 0) return;
    const used = new Set<string>();
    list.forEach((r, i) => {
      let bestIdx = -1;
      let bestDist = Number.POSITIVE_INFINITY;
      if (typeof r.y === "number") {
        imgs.forEach((img, k) => {
          if (used.has(img.id) || typeof img.y !== "number") return;
          const dist = Math.abs((img.y as number) - (r.y as number));
          if (dist < bestDist) {
            bestDist = dist;
            bestIdx = k;
          }
        });
      }
      if (bestIdx >= 0 && bestDist <= 140) {
        used.add(imgs[bestIdx].id);
        r.imageUrl = imgs[bestIdx].dataUrl;
      } else {
        r.imageUrl = imgs[i % imgs.length].dataUrl;
      }
    });
  });

  return {
    rows,
    images,
    pages: doc.numPages,
    suggestedCategory,
    warnings,
  };
};

// Compara contra el catálogo actual para marcar el estado de cada fila.
export const computeRowStatus = (
  row: PdfCatalogRow,
  existing: { sku: string; pricePublic: number; pv: number; imageUrl?: string }[]
): CatalogRowStatus => {
  const match = existing.find((p) => p.sku.toLowerCase() === row.sku.trim().toLowerCase());
  if (!match) return "nuevo";
  const priceDiff = row.pricePublic > 0 && Math.round(row.pricePublic) !== Math.round(match.pricePublic);
  const pvDiff = row.pv > 0 && row.pv !== match.pv;
  if (priceDiff || pvDiff) return "cambio";
  const newPhoto = !!row.imageUrl && row.imageUrl !== (match.imageUrl ?? "");
  if (newPhoto) return "foto";
  return "sin_cambios";
};